/**
 * Merges a per-type GS detail preview (CT-5092) into an inbox event's `detail`,
 * returning a new event. Only fills gaps — an existing in-memory value is never
 * blanked by a missing preview field. The full raw preview is kept under
 * `detail.preview` so the per-type bodies (CT-5095 / CT-5096) can read it
 * verbatim once they land.
 *
 * Field names mirror the beta MyWork preview responses:
 *  - claim suggestion → { claimText, context, polarity, versionState, status,
 *      hasPendingSuggestion, scoring, sourceDocument, versionHistory,
 *      linkedBundles, promotionHistory, description }
 *  - change request   → { proposedValueText, truthCurrentText, truthTitle,
 *      operationType, riskLevel, confidence, createdByName, proposedFactPatch:{
 *      title, statement, rationale } }. Field names verified live on 2026-09-01
 *      once change-request/preview came back online (was MWK500). `truthCurrentText`
 *      is the "before" side of the diff (null for CREATE_FACT, set for edits).
 */

import { INTERIM_EVENT_TYPE } from '../constants/interimEventTypes.js';
import { EVENT_TYPE } from '../constants/eventTypes.js';
import { initialsFromName } from './initials.js';
import {
  buildGovPromotionSituation,
  GOV_PROMOTION_PIPELINE,
} from './mapPromotionToEvent.js';
import {
  countConflictClaims,
  mapPromotionClaimsToUi,
} from './mapPromotionClaims.js';

function claimPreviewPatch(body) {
  return {
    claimText: body.claimText ?? null,
    context: body.context ?? null,
    polarity: body.polarity ?? null,
    versionState: body.versionState ?? null,
    status: body.status ?? null,
    hasPendingSuggestion: body.hasPendingSuggestion ?? null,
    scoring: body.scoring ?? null,
    sourceDocument: body.sourceDocument ?? null,
    versionHistory: body.versionHistory ?? null,
    linkedBundles: body.linkedBundles ?? null,
    promotionHistory: body.promotionHistory ?? null,
  };
}

function changeRequestPreviewPatch(body) {
  const patch = body.proposedFactPatch ?? {};
  return {
    title: patch.title ?? body.truthTitle ?? null,
    proposedStatement: patch.statement ?? body.proposedValueText ?? null,
    // The current fact text is `truthCurrentText` on the wire — the "before" side
    // of the before/after diff (null for CREATE_FACT, populated for edits). The
    // older currentStatement/currentValueText keys stay as fallbacks.
    currentStatement:
      body.truthCurrentText ?? body.currentStatement ?? body.currentValueText ?? null,
    rationale: patch.rationale ?? body.rationale ?? null,
    confidence: patch.confidence ?? body.confidence ?? null,
    riskLevel: patch.riskLevel ?? body.riskLevel ?? null,
    operationType: body.operationType ?? null,
    createdByName: body.createdByName ?? null,
  };
}

function govPromotionPreviewPatch(previewBody) {
  const details = previewBody.details ?? {};
  const metadata = details.metadata ?? {};
  const targetKb = details.targetKB ?? details.targetKnowledgeBase ?? {};
  const claimRows = previewBody.claims ?? [];
  const uiClaims = mapPromotionClaimsToUi(claimRows);
  const conflictCount = countConflictClaims(claimRows);
  const claimCount = metadata.claimCount ?? details.claimCounts?.total ?? claimRows.length;

  return {
    detailPatch: {
      claims: uiClaims,
      claimCount,
      conflictCount,
      claimCounts: details.claimCounts ?? null,
      metadata,
      sandboxContext: details.sandboxContext ?? null,
      targetKB: targetKb,
    },
    situation: buildGovPromotionSituation(
      claimCount,
      conflictCount,
      metadata.description ?? previewBody.details?.metadata?.description ?? null,
    ),
    destination:
      targetKb.knowledgebaseName ??
      targetKb.name ??
      previewBody.context?.knowledgebaseName ??
      null,
    pipeline: GOV_PROMOTION_PIPELINE,
  };
}

/** Drops keys whose value is nullish so a merge never overwrites a real value with null. */
function pruneNullish(obj) {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null));
}

/**
 * Adapts the GS preview owner ({ userUuid, fullName, photoFileUuid }) onto the UI
 * owner slot ({ id, name, initials, isMine }). `isMine` is not sent by GS, so it
 * stays false until the current user is resolved (CT-5094). Returns `undefined`
 * when there is no owner, so an existing owner is never blanked.
 */
export function mapPreviewOwner(owner) {
  if (!owner) return undefined;
  const id = owner.userUuid ?? owner.id ?? null;
  const name = owner.fullName ?? owner.name ?? null;
  if (!id && !name) return undefined;
  const initials = name ? initialsFromName(name) : null;
  return { id, name: name ?? id, initials, isMine: owner.isMine ?? owner.is_mine ?? false };
}

/**
 * @param {Object|null} event - the base in-memory event.
 * @param {Object|null} previewBody - the raw GS preview body (or null).
 * @returns {Object|null} a new event with `detail` enriched, or the input event.
 */
export function applyEventPreview(event, previewBody) {
  if (!event || !previewBody) return event;

  let patch;
  let owner;
  let govPromotionFields;
  if (event.type === INTERIM_EVENT_TYPE.CLAIM_SUGGESTION) {
    patch = claimPreviewPatch(previewBody);
    // Flatten scoring.confidence into detail.confidence (the UI slot) as a
    // fallback — the list row's confidence was often null on the wire.
    patch.confidence = event.detail?.confidence ?? previewBody.scoring?.confidence ?? null;
    // The claim list row carries no owner; the preview does — surface it so
    // My Team can show who holds the claim.
    owner = mapPreviewOwner(previewBody.owner);
  } else if (event.type === INTERIM_EVENT_TYPE.CHANGE_REQUEST) {
    patch = changeRequestPreviewPatch(previewBody);
  } else if (event.type === EVENT_TYPE.GOV_PROMOTION) {
    govPromotionFields = govPromotionPreviewPatch(previewBody);
    patch = govPromotionFields.detailPatch;
  } else {
    return event;
  }

  const description =
    event.description ||
    govPromotionFields?.situation ||
    previewBody.description ||
    previewBody.proposedValueText ||
    null;
  const situation =
    govPromotionFields?.situation ||
    event.situation ||
    previewBody.description ||
    previewBody.proposedValueText ||
    previewBody.claimText ||
    null;

  return {
    ...event,
    // Fill the owner from the preview ONLY when the event has none yet. A local
    // assignment (Take / Reassign sets event.owner) must not be clobbered when
    // the cached preview is re-applied.
    ...(owner !== undefined && event.owner == null ? { owner } : {}),
    description,
    situation,
    ...(govPromotionFields?.destination ? { destination: govPromotionFields.destination } : {}),
    ...(govPromotionFields?.pipeline ? { pipeline: govPromotionFields.pipeline } : {}),
    detail: { ...event.detail, ...pruneNullish(patch), preview: previewBody },
  };
}
